import React from "react";
import clsx from "clsx";

interface EditSaveButtonProps {
  isEditMode: boolean;
  onToggle: () => void;
  className?: string;
  position?: "fixed" | "relative" | "absolute";
  size?: "sm" | "md" | "lg";
  variant?: "orange" | "blue" | "green" | "purple" | "red";
  ariaLabel?: string;
  disabled?: boolean;
}

/** Size mappings */
const sizeClasses: Record<NonNullable<EditSaveButtonProps["size"]>, string> = {
  sm: "px-3 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm",
  md: "px-4 py-3 text-sm sm:px-5 sm:py-4 sm:text-base",
  lg: "px-5 py-4 text-base sm:px-6 sm:py-5 sm:text-lg",
};

const iconSizes: Record<NonNullable<EditSaveButtonProps["size"]>, string> = {
  sm: "w-4 h-4 sm:w-5 sm:h-5",
  md: "w-5 h-5 sm:w-6 sm:h-6",
  lg: "w-6 h-6 sm:w-7 sm:h-7",
};

/** Color variants */
const variantClasses: Record<
  NonNullable<EditSaveButtonProps["variant"]>,
  string
> = {
  orange:
    "bg-orange-500 hover:bg-orange-600 focus:ring-orange-300 dark:bg-orange-400 dark:hover:bg-orange-500",
  blue: "bg-blue-500 hover:bg-blue-600 focus:ring-blue-300 dark:bg-blue-400 dark:hover:bg-blue-500",
  green:
    "bg-green-500 hover:bg-green-600 focus:ring-green-300 dark:bg-green-400 dark:hover:bg-green-500",
  purple:
    "bg-purple-500 hover:bg-purple-600 focus:ring-purple-300 dark:bg-purple-400 dark:hover:bg-purple-500",
  red: "bg-[#EF4444] hover:bg-[#DC2626] focus:ring-red-300 dark:bg-[#EF4444] dark:hover:bg-[#DC2626]",
};

/** Position mappings */
const positionClasses: Record<
  NonNullable<EditSaveButtonProps["position"]>,
  string
> = {
  fixed: "fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50",
  relative: "relative",
  absolute: "absolute",
};

/** SVG Icons */
const SaveIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const EditIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const LoadingSpinner: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={`${className} animate-spin`}
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/** Main Component */
const EditSaveButton: React.FC<EditSaveButtonProps> = React.memo(
  ({
    isEditMode,
    onToggle,
    className,
    position = "fixed",
    size = "md",
    variant = "red",
    ariaLabel,
    disabled = false,
  }) => {
    const label = isEditMode ? "Save Changes" : "Edit Content";

    return (
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={clsx(
          "rounded-full shadow-lg transition-all duration-200 flex items-center space-x-2 text-white font-medium border-0",
          "hover:shadow-xl transform hover:scale-105 focus:outline-none",
          sizeClasses[size],
          variantClasses[variant],
          positionClasses[position],
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
        title={label}
        aria-label={ariaLabel ?? label}
        aria-pressed={isEditMode}
        aria-live="polite"
      >
        {disabled && isEditMode ? (
          <>
            <LoadingSpinner className={iconSizes[size]} />
            <span className="hidden sm:inline">Saving...</span>
          </>
        ) : isEditMode ? (
          <>
            <SaveIcon className={iconSizes[size]} />
            <span className="hidden sm:inline">Save</span>
          </>
        ) : (
          <>
            <EditIcon className={iconSizes[size]} />
            <span className="hidden sm:inline">Edit</span>
          </>
        )}
      </button>
    );
  }
);

EditSaveButton.displayName = "EditSaveButton";

export default EditSaveButton;
